import React from 'react';

interface CinematicLoaderProps {
  type?: 'hero' | 'card' | 'grid';
  showShimmer?: boolean;
  duration?: number;
}

export function CinematicLoader({ 
  type = 'hero', 
  showShimmer = true,
  duration = 2000 
}: CinematicLoaderProps) {
  
  if (type === 'hero') {
    return (
      <section className="relative h-[50vh] sm:h-[60vh] lg:h-[70vh] min-h-[400px] max-h-[800px] overflow-hidden mt-18">
        {/* Background Gradient Animation */}
        <div className="absolute inset-0 cinematic-bg-pulse" />
        
        {/* Shimmer Overlay */}
        {showShimmer && (
          <div className="absolute inset-0 cinematic-shimmer" 
               style={{ animationDuration: `${duration}ms` }} />
        )}
        
        {/* Content Skeleton */}
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center z-10">
          <div className="max-w-4xl text-white space-y-6">
            {/* Badge Skeleton */}
            <div className="inline-flex items-center px-4 py-2 rounded-full">
              <div className="w-2 h-2 bg-white/20 rounded-full mr-2 cinematic-pulse-slow" />
              <div className="h-4 w-32 bg-white/20 rounded cinematic-skeleton-wave" />
            </div>
            
            {/* Title Skeleton */}
            <div className="space-y-3">
              <div className="h-8 sm:h-10 md:h-12 lg:h-14 xl:h-16 w-full bg-white/20 rounded cinematic-skeleton-wave" 
                   style={{ animationDelay: '0.2s' }} />
              <div className="h-8 sm:h-10 md:h-12 lg:h-14 xl:h-16 w-4/5 bg-white/20 rounded cinematic-skeleton-wave" 
                   style={{ animationDelay: '0.4s' }} />
              <div className="h-8 sm:h-10 md:h-12 lg:h-14 xl:h-16 w-3/5 bg-white/15 rounded cinematic-skeleton-wave" 
                   style={{ animationDelay: '0.6s' }} />
            </div>
            
            {/* Description Skeleton */}
            <div className="space-y-2 max-w-2xl">
              <div className="h-5 sm:h-6 w-full bg-white/15 rounded cinematic-skeleton-wave" 
                   style={{ animationDelay: '0.8s' }} />
              <div className="h-5 sm:h-6 w-4/5 bg-white/15 rounded cinematic-skeleton-wave" 
                   style={{ animationDelay: '1.0s' }} />
            </div>
            
            {/* Button Skeleton */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <div className="h-12 w-40 bg-white/25 rounded-lg cinematic-skeleton-wave" 
                   style={{ animationDelay: '1.2s' }} />
              <div className="h-6 w-48 bg-white/10 rounded cinematic-skeleton-wave hidden sm:block" 
                   style={{ animationDelay: '1.4s' }} />
            </div>
          </div>
        </div>

        {/* Navigation Skeleton */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/15 rounded-full cinematic-pulse-slow" />
        </div>
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/15 rounded-full cinematic-pulse-slow" 
               style={{ animationDelay: '0.2s' }} />
        </div>

        {/* Indicators Skeleton */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
          <div className="flex space-x-2 bg-black/20 backdrop-blur-sm rounded-full px-3 py-2">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className="w-3 h-3 bg-white/20 rounded-full cinematic-pulse-slow"
                style={{ animationDelay: `${index * 0.2}s` }}
              />
            ))}
          </div>
        </div>

        {/* Progress Bar Skeleton */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
          <div className="h-full bg-gradient-to-r from-orange-primary/30 to-orange-primary/10 cinematic-progress-wave" />
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white/10 rounded-full cinematic-float"
              style={{
                left: `${15 + i * 12}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${3 + (i % 2)}s`
              }}
            />
          ))}
        </div>
      </section>
    );
  }

  if (type === 'card') {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden cinematic-card-skeleton">
        {/* Image Skeleton */}
        <div className="aspect-event-card bg-muted relative overflow-hidden">
          <div className="absolute inset-0 cinematic-bg-pulse" />
          {showShimmer && <div className="absolute inset-0 cinematic-shimmer" />}
        </div>
        
        {/* Content Skeleton */}
        <div className="p-4 space-y-3">
          {/* Title */}
          <div className="h-6 bg-muted rounded cinematic-skeleton-wave" />
          <div className="h-6 w-4/5 bg-muted/70 rounded cinematic-skeleton-wave" 
               style={{ animationDelay: '0.2s' }} />
          
          {/* Description */}
          <div className="space-y-2">
            <div className="h-4 bg-muted/50 rounded cinematic-skeleton-wave" 
                 style={{ animationDelay: '0.4s' }} />
            <div className="h-4 w-3/4 bg-muted/50 rounded cinematic-skeleton-wave" 
                 style={{ animationDelay: '0.6s' }} />
          </div>
          
          {/* Price and Button */}
          <div className="flex items-center justify-between pt-2">
            <div className="h-6 w-20 bg-muted/60 rounded cinematic-skeleton-wave" 
                 style={{ animationDelay: '0.8s' }} />
            <div className="h-9 w-24 bg-muted/40 rounded cinematic-skeleton-wave" 
                 style={{ animationDelay: '1.0s' }} />
          </div>
        </div>
      </div>
    );
  }

  // Grid skeleton
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {Array.from({ length: 8 }).map((_, index) => (
        <CinematicLoader 
          key={index} 
          type="card" 
          showShimmer={showShimmer}
        />
      ))}
    </div>
  );
}